import { InfoIcon } from 'lucide-react'

import logoUrl from '@/assets/myjobglasses.svg'
import { Badge } from '@/components/ui/Badge/Badge'
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction
} from '@/components/ui/AlertDialog/AlertDialog'
import { useMeetingStore } from '@stores/meeting'
import { useGetAppointment } from '@/graphql/meeting/useAppointment'
import { getInitials } from '@/utils/string.utils'
import { formatPhoneNumber } from '@/utils/phone.utils'

type MeetingHeaderProps = {
  showVideocallTag?: boolean
  showMeetingInfo?: boolean
  centered?: boolean
  phoneNumber?: string | null
  interlocutorFirstName?: string | null
}

type AvatarDisplayProps = {
  avatar?: string
  firstName?: string
  isLoading?: boolean
}

function AvatarDisplay({ avatar, firstName, isLoading }: AvatarDisplayProps) {
  if (isLoading) {
    return <div className="h-10 w-10 bg-primary-dark rounded-full animate-pulse" />
  }

  if (!avatar && !firstName) {
    return <div className="h-10 w-10 rounded-full bg-surface-tertiary" />
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-surface-tertiary">
      {avatar ? (
        <img src={avatar} alt="User avatar" className="h-full w-full object-cover" />
      ) : (
        <span className="text-lg font-semibold text-primary">{getInitials(firstName || '')}</span>
      )}
    </div>
  )
}

export function MeetingHeader({
  showVideocallTag,
  showMeetingInfo,
  centered,
  phoneNumber,
  interlocutorFirstName
}: MeetingHeaderProps) {
  const appointmentId = useMeetingStore((s) => s.appointmentId)

  const { appointment, loading } = useGetAppointment(appointmentId || '')

  const appointmentTitle =
    showMeetingInfo && appointment?.interlocutor?.firstName
      ? `Rendez-vous My Job Glasses avec ${appointment.interlocutor.firstName}`
      : null

  const userAvatar = appointment?.me?.avatar
  const userFirstName = appointment?.me?.firstName

  const hasPhoneInfo = phoneNumber || interlocutorFirstName

  if (centered) {
    return (
      <div className={`flex items-center px-4 py-3 ${hasPhoneInfo ? 'justify-between' : 'justify-center'}`}>
        <img src={logoUrl} alt="myjobglasses" className="w-auto h-[24px] md:h-[32px]" />

        {hasPhoneInfo && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button aria-label="Informations sur le rendez-vous">
                <InfoIcon className="h-5 w-5 text-white" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rendez-vous My Job Glasses</AlertDialogTitle>
                <AlertDialogDescription>
                  Cet appel est enregistré et transcrit automatiquement. Si vous rencontrez un problème de connexion,
                  vous pouvez appeler {interlocutorFirstName}
                  {phoneNumber ? ` sur son numéro personnel au ${formatPhoneNumber(phoneNumber)}` : '.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction>Fermer</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    )
  }

  return (
    <div className="relative flex flex-col md:flex-row items-center justify-between px-4 md:px-10 py-3 md:py-4 bg-primary gap-3 md:gap-0">
      {/* LEFT: Logo + Badge */}
      <div className="flex items-center gap-3 md:gap-6 w-full md:w-auto justify-between md:justify-start">
        <img src={logoUrl} alt="myjobglasses" className="w-auto h-[24px] md:h-[32px]" />

        {showVideocallTag && (
          <Badge variant="secondary" className="flex bg-white p-2 font-normal px-6">
            Visio intégrée
          </Badge>
        )}
      </div>

      {/* MIDDLE: Appointment Title */}
      {appointmentTitle && (
        <div className="w-full md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:pointer-events-none text-center">
          <span className="text-white text-xs md:text-sm font-medium whitespace-normal md:whitespace-nowrap">
            {appointmentTitle}
          </span>
        </div>
      )}

      {/* RIGHT: User Avatar (desktop only) */}
      {showMeetingInfo && (
        <div className="hidden md:flex items-center">
          <AvatarDisplay avatar={userAvatar} firstName={userFirstName} isLoading={loading} />
        </div>
      )}
    </div>
  )
}
